import { settings } from './storage.js';

/* Animación de arranque: las tres cintas del logo se encienden una a una, se cierran en el triángulo de play y dan paso a
   la app (unos 3,5 s). Va por encima de todo mientras la app carga por detrás; se salta con cualquier toque o tecla y se
   apaga en Ajustes. El sonido se genera en el aparato (sin audio con derechos). En las apps sale en cada arranque; en la web,
   una vez por sesión del navegador y solo al entrar por la portada (no en un enlace a una ficha o a un vídeo). */

export const INTRO_MS = 3650;
let current = null;

/** ¿Debe verse la animación ahora? Función pura para poder probarla. */
export function introPlan({ standalone = true, web = false, enabled = true, reducedMotion = false, hidden = false, search = '', seenSession = false, entryHome = true } = {}) {
    if (!enabled || reducedMotion || hidden) return false;
    if (/[?&](nointro|nosw)\b/.test(search || '')) return false;
    if (standalone) return true;
    // Web / PWA: solo la primera vez en la sesión del navegador y al entrar por la portada.
    return !!web && !seenSession && !!entryHome;
}

const CSS = `
#app-intro{position:fixed;inset:0;z-index:100000;background:#000;overflow:hidden;container-type:size;touch-action:none;-webkit-user-select:none;user-select:none}
#app-intro .in-glow{position:absolute;inset:-20%;background:radial-gradient(closest-side at 50% 46%,rgba(90,110,255,.38),rgba(160,60,255,.16) 45%,transparent 72%);opacity:0}
@font-face{font-family:'Outfit';src:url(fonts/outfit-500.woff2) format('woff2');font-weight:500;font-style:normal;font-display:block}
#app-intro .in-logo{position:absolute;left:50%;top:44%;width:clamp(120px,max(34cqw,min(48cqh,60cqw)),400px);aspect-ratio:1;transform:translate(-50%,-50%);isolation:isolate}
#app-intro .in-ly,#app-intro .in-shine{position:absolute;inset:0;width:100%;height:100%;will-change:transform,opacity,filter}
#app-intro.is-blend .in-ly,#app-intro.is-blend .in-shine{mix-blend-mode:plus-lighter}
#app-intro.is-screen .in-ly,#app-intro.is-screen .in-shine{mix-blend-mode:screen}
#app-intro .in-shine{-webkit-mask-size:contain;mask-size:contain;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;background:linear-gradient(105deg,transparent 38%,rgba(255,255,255,.9) 50%,transparent 62%);background-size:300% 100%;background-position:130% 0;opacity:0}
#app-intro .in-word{position:absolute;left:0;right:0;top:calc(44cqh + clamp(60px,max(17cqw,min(24cqh,30cqw)),200px) + 3.4cqh);text-align:center;font:500 clamp(16px,min(4.4cqw,6cqh),40px)/1 'Outfit',ui-rounded,'Segoe UI',system-ui,sans-serif;letter-spacing:.14em;color:#f1f2fa;padding-left:.14em;opacity:0}
#app-intro .in-flash{position:absolute;inset:0;background:radial-gradient(closest-side at 50% 44%,rgba(255,255,255,.95),rgba(180,200,255,.4) 40%,transparent 70%);opacity:0;mix-blend-mode:screen}
`;

function noiseBuffer(c, secs) { const n = Math.floor(c.sampleRate * secs); const b = c.createBuffer(1, n, c.sampleRate); const d = b.getChannelData(0); for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1; return b; }
function impulse(c, secs, decay) { const n = Math.floor(c.sampleRate * secs); const b = c.createBuffer(2, n, c.sampleRate); for (let ch = 0; ch < 2; ch++) { const d = b.getChannelData(ch); for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, decay); } return b; }

/** Sonido propio, sintetizado: barrido de aire, tres notas (la, do#, mi), golpe suave y acorde de la mayor. Devuelve el nodo maestro. */
export function synthIntro(c, t0) {
    const master = c.createGain();
    master.gain.value = 0.0001;
    master.gain.exponentialRampToValueAtTime(0.55, t0 + 0.08);
    master.gain.setValueAtTime(0.55, t0 + 3.0);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + 4.0);
    const comp = c.createDynamicsCompressor(); comp.threshold.value = -14; comp.ratio.value = 4;
    const dry = c.createGain(); const wet = c.createGain(); const rev = c.createConvolver();
    rev.buffer = impulse(c, 2.4, 2.6); dry.gain.value = 0.85; wet.gain.value = 0.32;
    master.connect(dry); master.connect(rev); rev.connect(wet); dry.connect(comp); wet.connect(comp); comp.connect(c.destination);
    const tone = (type, freq, start, dur, peak, attack) => {
        const o = c.createOscillator(); const g = c.createGain(); o.type = type; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, t0 + start); g.gain.exponentialRampToValueAtTime(peak, t0 + start + attack); g.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
        o.connect(g); g.connect(master); o.start(t0 + start); o.stop(t0 + start + dur + 0.05);
    };
    const bell = (freq, start, peak) => { tone('sine', freq, start, 1.5, peak, 0.012); tone('sine', freq * 2.01, start, 0.9, peak * 0.32, 0.01); tone('sine', freq * 3.02, start, 0.5, peak * 0.12, 0.008); };
    const nz = c.createBufferSource(); nz.buffer = noiseBuffer(c, 2);
    const bp = c.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.1; bp.frequency.setValueAtTime(260, t0); bp.frequency.exponentialRampToValueAtTime(3600, t0 + 1.5);
    const ng = c.createGain(); ng.gain.setValueAtTime(0.0001, t0); ng.gain.exponentialRampToValueAtTime(0.2, t0 + 1.0); ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.75);
    nz.connect(bp); bp.connect(ng); ng.connect(master); nz.start(t0); nz.stop(t0 + 2);
    tone('sine', 55, 0, 1.7, 0.26, 1.3);
    bell(659.25, 0.30, 0.20); bell(880, 0.62, 0.18); bell(1108.73, 0.94, 0.17);
    const th = c.createOscillator(); const tg = c.createGain(); th.type = 'sine';
    th.frequency.setValueAtTime(130, t0 + 1.5); th.frequency.exponentialRampToValueAtTime(46, t0 + 1.9);
    tg.gain.setValueAtTime(0.0001, t0 + 1.5); tg.gain.exponentialRampToValueAtTime(0.55, t0 + 1.53); tg.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.05);
    th.connect(tg); tg.connect(master); th.start(t0 + 1.5); th.stop(t0 + 2.1);
    [[220, 0.09], [329.63, 0.08], [440, 0.075], [554.37, 0.06], [659.25, 0.05]].forEach((n) => tone('triangle', n[0], 1.5, 2.4, n[1], 0.06));
    const sp = c.createBufferSource(); sp.buffer = noiseBuffer(c, 0.5);
    const hp = c.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.setValueAtTime(7000, t0 + 1.6); hp.frequency.exponentialRampToValueAtTime(3000, t0 + 1.95);
    const sg = c.createGain(); sg.gain.setValueAtTime(0.0001, t0 + 1.6); sg.gain.exponentialRampToValueAtTime(0.07, t0 + 1.64); sg.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.95);
    sp.connect(hp); hp.connect(sg); sg.connect(master); sp.start(t0 + 1.6); sp.stop(t0 + 2.1);
    return master;
}

function loadImage(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
}

/** Para la animación al momento (un enlace directo, el reproductor que se abre, un toque o una tecla). */
export function skipIntro() {
    if (current) current.skip();
}

export function introActive() {
    return !!current;
}

/**
 * Pone la animación por encima de la app. `sound`: con sonido. `preview`: vista previa desde Ajustes (no depende del arranque).
 * Devuelve una promesa que se cumple cuando termina o se salta.
 */
export function startIntro({ sound = true, base = 'img/' } = {}) {
    if (current || typeof document === 'undefined') return Promise.resolve();
    const root = document.createElement('div');
    root.id = 'app-intro';
    root.setAttribute('aria-hidden', 'true');
    // Las tres cintas se suman con luz (plus-lighter); donde no existe, con «screen», que se le parece. Siempre tres capas:
    // la versión de una sola imagen no era la misma animación.
    const sup = (v) => !!(typeof CSS !== 'undefined' && CSS.supports && CSS.supports('mix-blend-mode', v));
    root.classList.add(sup('plus-lighter') ? 'is-blend' : 'is-screen');
    const style = document.createElement('style');
    style.textContent = CSS;
    root.appendChild(style);
    root.insertAdjacentHTML('beforeend', '<div class="in-glow"></div><div class="in-logo"></div><div class="in-word">Filoteca</div><div class="in-flash"></div>');
    try { if (document.fonts && document.fonts.load) document.fonts.load("500 20px 'Outfit'").catch(() => {}); } catch (e) { /* sin API de fuentes */ }
    document.body.appendChild(root);

    let anims = [];
    let ctx = null;
    let master = null;
    let finished = false;
    let doneResolve = null;
    const done = new Promise((resolve) => { doneResolve = resolve; });
    const timers = [];

    const finish = (fast) => {
        if (finished) return;
        finished = true;
        timers.forEach(clearTimeout);
        if (master && ctx) { try { master.gain.cancelScheduledValues(ctx.currentTime); master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.05); } catch (e) { /* ya parado */ } }
        const remove = () => {
            anims.forEach((a) => { try { a.cancel(); } catch (e) { /* nada */ } });
            window.removeEventListener('keydown', onKey, true);
            root.removeEventListener('pointerdown', onTap, true);
            root.remove();
            if (ctx) { try { ctx.close(); } catch (e) { /* nada */ } }
            current = null;
            doneResolve();
        };
        if (fast && root.animate) { const from = Number(getComputedStyle(root).opacity) || 1; const a = root.animate([{ opacity: from }, { opacity: 0 }], { duration: 220, fill: 'forwards' }); a.onfinish = remove; timers.push(setTimeout(remove, 400)); } else remove();
    };
    const onKey = (e) => { e.preventDefault(); e.stopPropagation(); finish(true); };
    const onTap = (e) => { e.preventDefault(); e.stopPropagation(); finish(true); };
    window.addEventListener('keydown', onKey, true);
    root.addEventListener('pointerdown', onTap, true);
    current = { skip: () => finish(true) };

    const run = async () => {
        const [a, b, c, full] = await Promise.all(['intro-a.webp', 'intro-b.webp', 'intro-c.webp', 'intro-full.webp'].map((n) => loadImage(base + n)));
        if (finished) return;
        if (!a || !b || !c || !full || !root.animate) { finish(false); return; }
        const logo = root.querySelector('.in-logo');
        const layers = [a, b, c];
        layers.forEach((img) => { img.className = 'in-ly'; img.alt = ''; img.draggable = false; logo.appendChild(img); });
        const shine = document.createElement('div');
        shine.className = 'in-shine';
        shine.style.webkitMaskImage = 'url(' + full.src + ')'; shine.style.maskImage = 'url(' + full.src + ')';
        logo.appendChild(shine);
        const q = (s) => root.querySelector(s);
        const an = (el, frames, opts) => { const x = el.animate(frames, Object.assign({ fill: 'both' }, opts)); anims.push(x); return x; };
        const EASE = 'cubic-bezier(.2,.8,.2,1)';
        an(q('.in-glow'), [{ opacity: 0 }, { opacity: 0.85, offset: 0.5 }, { opacity: 0.5 }], { duration: 1700 });
        an(a, [{ transform: 'translate(-16%,-12%) scale(1.14)', filter: 'blur(16px)', opacity: 0 }, { transform: 'none', filter: 'blur(0)', opacity: 1 }], { duration: 800, delay: 200, easing: EASE });
        an(b, [{ transform: 'translate(-4%,20%) rotate(-8deg) scale(.9)', filter: 'blur(16px)', opacity: 0 }, { transform: 'none', filter: 'blur(0)', opacity: 1 }], { duration: 800, delay: 500, easing: EASE });
        an(c, [{ transform: 'translate(16%,10%) rotate(6deg) scale(.88)', filter: 'blur(16px)', opacity: 0 }, { transform: 'none', filter: 'blur(0)', opacity: 1 }], { duration: 800, delay: 800, easing: EASE });
        an(shine, [{ opacity: 1, backgroundPosition: '130% 0' }, { opacity: 1, backgroundPosition: '-30% 0' }], { duration: 750, delay: 1600, easing: 'ease-in-out', fill: 'forwards' });
        an(logo, [{ transform: 'translate(-50%,-50%) scale(1)' }, { transform: 'translate(-50%,-50%) scale(1.06)', offset: 0.35 }, { transform: 'translate(-50%,-50%) scale(1)', offset: 0.7 }, { transform: 'translate(-50%,-50%) scale(1)' }], { duration: 700, delay: 1500, easing: 'ease-out' });
        an(logo, [{ transform: 'translate(-50%,-50%) scale(1)', opacity: 1 }, { transform: 'translate(-50%,-50%) scale(1.9)', opacity: 0 }], { duration: 650, delay: 2900, easing: 'cubic-bezier(.5,0,.9,.4)', fill: 'forwards' });
        an(q('.in-flash'), [{ opacity: 0 }, { opacity: 0.55, offset: 0.25 }, { opacity: 0 }], { duration: 650, delay: 1500 });
        // Solo el símbolo: Alfredo (05-10) no quiere la palabra «Filoteca» bajo el logo (el .in-word queda en el DOM, invisible).
        an(root, [{ opacity: 1 }, { opacity: 0 }], { duration: 600, delay: 2950, fill: 'forwards' });
        if (sound) {
            try {
                const AC = window.AudioContext || window.webkitAudioContext;
                if (AC) {
                    ctx = new AC();
                    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
                    master = synthIntro(ctx, ctx.currentTime + 0.05);
                }
            } catch (e) { /* sin audio: se ve igual */ }
        }
        timers.push(setTimeout(() => finish(false), INTRO_MS));
    };
    run().catch(() => finish(false));
    return done;
}

/** Arranque de la app: decide si toca la animación y la lanza. */
export function maybeStartIntro({ standalone = false, web = false } = {}) {
    let seenSession = false;
    try { seenSession = !!sessionStorage.getItem('filoteca-intro'); } catch (e) { /* sin sessionStorage */ }
    const path = typeof location !== 'undefined' ? String(location.pathname || '/').replace(/^\/app(?=\/|$)/, '') || '/' : '/';
    const plan = introPlan({
        standalone, web,
        enabled: settings.get('introAnim') !== false,
        reducedMotion: typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
        hidden: typeof document !== 'undefined' && document.hidden,
        search: typeof location !== 'undefined' ? location.search : '',
        seenSession,
        entryHome: /^\/?$/.test(path.replace(/\/index\.html$/, '/'))
    });
    if (!plan) return Promise.resolve();
    try { sessionStorage.setItem('filoteca-intro', '1'); } catch (e) { /* nada */ }
    return startIntro({ sound: settings.get('introSound') !== false });
}
